import React from 'react';
import { FormGrid } from '../profile/profile.styled';
import { Panel } from '../settings.styled';
import {
  Title,
  ChannelCard,
  ChannelLabel,
  ChannelMeta,
  ChannelCheckbox,
  ConsentBox,
  ConsentText,
  InlineLink,
  Actions,
  ConfirmBtn,
  CancelBtn,
  Block,
} from './notification.styled';
import { useNotificationsSettings } from './notification.hooks';

type Props = {
  initialNotificationMethod?: string | null;
  initialSmsConsent?: boolean;
  onSettingsChanged?: (method: string | null, consent: boolean) => void;
};

export const NotificationsSettings: React.FC<Props> = ({
  initialNotificationMethod,
  initialSmsConsent,
  onSettingsChanged,
}) => {
  const {
    email,
    sms,
    smsConsented,
    showConsent,
    handleEmailCardClick,
    handleSmsCardClick,
    handleEmailChange,
    handleSmsChange,
    handleCancelSmsConsent,
    handleConfirmSmsConsent,
    pushEnabled,
    pushLoading,
    pushSupported,
    pushDenied,
    handlePushCardClick,
    handlePushChange,
  } = useNotificationsSettings({ initialNotificationMethod, initialSmsConsent, onSettingsChanged });

  return (
    <Panel>
      <FormGrid>
        <Block>
          <Title id="notif-methods-title">
            Choose how you&apos;d like to receive notifications
          </Title>
          {/* Email card */}
          <ChannelCard role="group" aria-labelledby="email-option" onClick={handleEmailCardClick}>
            <ChannelCheckbox
              id="email-option"
              type="checkbox"
              checked={email}
              onChange={handleEmailChange}
              onClick={(e) => e.stopPropagation()}
              aria-labelledby="email-label"
            />
            <ChannelLabel id="email-label" onClick={(e) => e.stopPropagation()}>
              Email
            </ChannelLabel>
          </ChannelCard>
          {/* SMS card */}
          <ChannelCard role="group" aria-labelledby="sms-option" onClick={handleSmsCardClick}>
            <ChannelCheckbox
              id="sms-option"
              type="checkbox"
              checked={sms}
              onChange={handleSmsChange}
              onClick={(e) => e.stopPropagation()}
              aria-labelledby="sms-label"
            />
            <ChannelLabel id="sms-label" onClick={(e) => e.stopPropagation()}>
              Text/SMS
            </ChannelLabel>

            {!smsConsented && (
              <ChannelMeta aria-live="polite">
                <span>Requires consent</span>
                <img
                  src="/lock.svg"
                  style={{
                    height: '16px',
                  }}
                />
              </ChannelMeta>
            )}
          </ChannelCard>
          {/* Push card */}
          <ChannelCard
            role="group"
            aria-labelledby="push-option"
            onClick={handlePushCardClick}
            style={{ opacity: !pushSupported || pushDenied ? 0.5 : 1, cursor: !pushSupported || pushDenied ? 'default' : 'pointer' }}
          >
            <ChannelCheckbox
              id="push-option"
              type="checkbox"
              checked={pushEnabled}
              onChange={handlePushChange}
              onClick={(e) => e.stopPropagation()}
              disabled={!pushSupported || pushDenied || pushLoading}
              aria-labelledby="push-label"
            />
            <ChannelLabel id="push-label" onClick={(e) => e.stopPropagation()}>
              Push Notifications
            </ChannelLabel>

            {pushDenied && (
              <ChannelMeta aria-live="polite">
                <span>Blocked in browser</span>
              </ChannelMeta>
            )}
            {!pushSupported && !pushDenied && (
              <ChannelMeta aria-live="polite">
                <span>Not supported</span>
              </ChannelMeta>
            )}
          </ChannelCard>
          {/* Consent panel (only for SMS) */}
          {showConsent && (
            <>
              <ConsentBox>
                <ConsentText>
                  By enabling SMS notifications, you agree to receive text messages from Mailander as described in our <InlineLink href="/terms" target="_blank" rel="noreferrer"> Terms of Use </InlineLink>{' '} and <InlineLink href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</InlineLink>. Message frequency varies. Msg & Data rates may apply. Reply STOP to opt out.
                </ConsentText>
              </ConsentBox>

              <Actions>
                <CancelBtn type="button" onClick={handleCancelSmsConsent}>
                  Cancel
                </CancelBtn>
                <ConfirmBtn type="button" onClick={handleConfirmSmsConsent}>
                  Confirm &amp; enable
                </ConfirmBtn>
              </Actions>
            </>
          )}
        </Block>
      </FormGrid>
    </Panel>
  );
};
